import { configStore, uiStore } from "@/store/store-accessor";
import { PanelPosition, UIPanel } from "@/types/ui";
import { runCallbackIfElementIsNotVisibleInRoot } from "@/util/dom";
import Vue from "vue";
import { readFromLocalStorage, writeToLocalStorage } from "@/util/localstorage";
import { objectGetTypedKeys } from "@/util/type-assertions";

export const DEFAULT_POPOUT_WIDTH = 280;
export const DEFAULT_OXYGEN_POPOUT_WIDTH = 265;

export function openUiPanel(panel: UIPanel) {
  const side = whichSideToPutUiPanel(panel);

  if (side === "left") {
    // Do not attempt opening already opened panel
    if (uiStore.panelState.leftSidebarPanel !== panel) {
      if (uiStore.panelState.leftSidebarPanel !== "closed") {
        const closed = closeUiPanel(uiStore.panelState.leftSidebarPanel);
        if (closed) {
          uiStore.setLeftSidebarState(panel);
        } // Do nothing if failed to close
      } else {
        uiStore.setLeftSidebarState(panel);
      }
    }
  }

  if (side === "right") {
    // Do not attempt opening already opened panel
    if (uiStore.panelState.rightSidebarPanel !== panel) {
      if (uiStore.panelState.rightSidebarPanel !== "closed") {
        const closed = closeUiPanel(uiStore.panelState.rightSidebarPanel);
        if (closed) {
          uiStore.setRightSidebarState(panel);
        } // Do nothing if failed to close
      } else {
        uiStore.setRightSidebarState(panel);
      }
    }
  }
}

export function isUiPanelOpen(panel: UIPanel) {
  return (
    uiStore.panelState.leftSidebarPanel === panel ||
    uiStore.panelState.rightSidebarPanel === panel
  );
}

export function toggleUiPanel(panel: UIPanel) {
  if (isUiPanelOpen(panel)) {
    closeUiPanel(panel);
  } else {
    openUiPanel(panel);
  }
}

export function closeUiPanel(panel: UIPanel): boolean {
  const confirmation = uiStore.closingConfirmations[panel];
  if (confirmation && !confirmation.chosenOption) {
    uiStore.showClosingConfirmation(panel);
    return false;
  }

  if (uiStore.panelState.leftSidebarPanel === panel) {
    uiStore.setLeftSidebarState("closed");
  }

  if (uiStore.panelState.rightSidebarPanel === panel) {
    uiStore.setRightSidebarState("closed");
  }

  return true;
}

export function closeAllUiPanels(): boolean {
  if (uiStore.panelState.leftSidebarPanel !== "closed") {
    const closed = closeUiPanel(uiStore.panelState.leftSidebarPanel);
    if (!closed) {
      return false;
    }
  }
  if (uiStore.panelState.rightSidebarPanel !== "closed") {
    const closed = closeUiPanel(uiStore.panelState.rightSidebarPanel);
    if (!closed) {
      return false;
    }
  }

  return true;
}

export const uiPanelDefaultPositions: Record<UIPanel, PanelPosition> = {
  add: "left",
  elementproperties: "left",
  structure: "right",
  history: "right",
  selectors: "right",
  oxySelectors: "right",
  globalSettings: "right",
  variables: "right",
  presets: "right",
  ai: "right"
};

export const uiOxygenPanelDefaultPositions: Record<UIPanel, PanelPosition> = {
  ...uiPanelDefaultPositions,
  structure: "left",
  elementproperties: "right"
};

export const uiWidePanels: UIPanel[] = ["variables"];

export function getDefaultPanelPosition(panel: UIPanel): PanelPosition {
  if (configStore.builderMode === "oxygen") {
    return uiOxygenPanelDefaultPositions[panel];
  }

  return uiPanelDefaultPositions[panel];
}

export function resetUiPanelPositionsToDefault() {
  objectGetTypedKeys(uiPanelDefaultPositions).forEach(uiPanel =>
    savePanelPositionPreferenceToLocalStorage(
      uiPanel,
      getDefaultPanelPosition(uiPanel)
    )
  );
}

export function whichSideToPutUiPanel(panel: UIPanel): PanelPosition {
  const userPreference = getPanelPositionPreferenceFromLocalStorage(panel);

  return userPreference || getDefaultPanelPosition(panel);
}

export function savePanelPositionPreferenceToLocalStorage(
  panel: UIPanel,
  position: PanelPosition
) {
  writeToLocalStorage(`uipanel_position_${panel}`, position);
}

export function getPanelPositionPreferenceFromLocalStorage(panel: UIPanel) {
  const position = readFromLocalStorage(`uipanel_position_${panel}`);
  if (position === "left") {
    return "left";
  } else if (position === "right") {
    return "right";
  }

  return getDefaultPanelPosition(panel);
}

export function scrollToHtmlElementInsidePanel(
  panelDomElement: Element,
  panelHeaderDomElement: Element,
  elementToScrollTo: Element
): void {
  Vue.nextTick(() => {
    const headerSize = panelHeaderDomElement.getBoundingClientRect();
    runCallbackIfElementIsNotVisibleInRoot(
      () => {
        elementToScrollTo.scrollIntoView({
          block: "center"
        });
      },
      elementToScrollTo,
      {
        root: panelDomElement,
        rootMargin: `-${headerSize.height}px 0px 0px 0px`
      }
    );
  });
}
